import { useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { 
  Bold, 
  Italic, 
  Heading2, 
  Heading3, 
  List, 
  ListOrdered, 
  Link, 
  Quote,
  Undo,
  Redo,
  Code
} from "lucide-react";

/**
 * Simple Rich Text Editor
 * Uses contentEditable with execCommand for basic formatting
 * Outputs HTML string
 */
export default function RichTextEditor({ value, onChange, placeholder = "Write your content..." }) {
  const editorRef = useRef(null);

  const execCommand = useCallback((command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
    // Trigger onChange after command
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  }, [onChange]);

  const handleInput = useCallback(() => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  }, [onChange]);

  const handlePaste = useCallback((e) => {
    e.preventDefault();
    const text = e.clipboardData.getData("text/plain");
    document.execCommand("insertText", false, text);
  }, []);

  const insertLink = useCallback(() => {
    const url = prompt("Enter URL:");
    if (url) {
      execCommand("createLink", url);
    }
  }, [execCommand]);

  const formatBlock = useCallback((tag) => {
    execCommand("formatBlock", tag);
  }, [execCommand]);

  const toolbarButtons = [
    { icon: Bold, command: () => execCommand("bold"), title: "Bold (Ctrl+B)" },
    { icon: Italic, command: () => execCommand("italic"), title: "Italic (Ctrl+I)" },
    { divider: true },
    { icon: Heading2, command: () => formatBlock("h2"), title: "Heading 2" },
    { icon: Heading3, command: () => formatBlock("h3"), title: "Heading 3" },
    { divider: true },
    { icon: List, command: () => execCommand("insertUnorderedList"), title: "Bullet List" },
    { icon: ListOrdered, command: () => execCommand("insertOrderedList"), title: "Numbered List" },
    { divider: true },
    { icon: Link, command: insertLink, title: "Insert Link" },
    { icon: Quote, command: () => formatBlock("blockquote"), title: "Quote" },
    { icon: Code, command: () => execCommand("formatBlock", "pre"), title: "Code Block" },
    { divider: true },
    { icon: Undo, command: () => execCommand("undo"), title: "Undo" },
    { icon: Redo, command: () => execCommand("redo"), title: "Redo" },
  ];

  return (
    <div className="border rounded-lg overflow-hidden" data-testid="rich-text-editor">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-1 p-2 bg-muted/50 border-b">
        {toolbarButtons.map((btn, idx) => 
          btn.divider ? (
            <div key={idx} className="w-px h-6 bg-border mx-1" />
          ) : (
            <Button
              key={idx}
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={btn.command}
              title={btn.title}
            >
              <btn.icon className="w-4 h-4" />
            </Button>
          )
        )}
      </div>

      {/* Editor */}
      <div
        ref={editorRef}
        contentEditable
        dir="ltr"
        className="min-h-[300px] p-4 focus:outline-none prose prose-sm max-w-none text-left
          prose-headings:font-heading prose-headings:font-semibold
          prose-h2:text-xl prose-h2:mt-6 prose-h2:mb-3
          prose-h3:text-lg prose-h3:mt-4 prose-h3:mb-2
          prose-p:my-2
          prose-ul:my-2 prose-ol:my-2
          prose-li:my-0.5
          prose-blockquote:border-l-primary prose-blockquote:bg-muted/30 prose-blockquote:py-1 prose-blockquote:px-3 prose-blockquote:my-3
          prose-pre:bg-muted prose-pre:p-3 prose-pre:rounded
          prose-a:text-primary prose-a:underline
          [&:empty]:before:content-[attr(data-placeholder)] [&:empty]:before:text-muted-foreground [&:empty]:before:pointer-events-none"
        style={{ direction: 'ltr', unicodeBidi: 'plaintext' }}
        data-placeholder={placeholder}
        onInput={handleInput}
        onPaste={handlePaste}
        dangerouslySetInnerHTML={{ __html: value || "" }}
        data-testid="editor-content"
      />
    </div>
  );
}
